import { Form } from "antd/es";
import { FormInstance, Rule } from "antd/es/form";
import * as React from "react";
import { KModal } from "../k-modal/k-modal";
import { KModalFooter } from "../k-modal/k-modal-footer";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../kuika-button/kuika-button";
import KuikaInput from "../kuika-input/kuika-input";
import KuikaModalIconWrapper, {
  IKuikaModalIconWrapperProps,
  KuikaModalIconIntent
} from "../kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaH5 from "../typography/H5/H5";
import KuikaText from "../typography/Text/Text";
import "./kuika-name-modal.scss";

/**
 * "Name the thing you are creating" — the designer's create modals are one family (an icon,
 * a centred heading, one validated name field, cancel/confirm), and they had drifted into
 * three near-copies. This is that family as one primitive: callers own the NAME RULES (an
 * entity's pattern and its uniqueness check are its own business) and nothing else.
 *
 * Validation stays antd `Rule[]` rather than a bespoke callback because these forms already
 * rely on antd's async validators, its error placement and its submit gating.
 */

export interface KuikaNameModalProps {
  visible: boolean;
  /** Heading copy — say what is being created, not "New item". */
  title: string;
  /** One line under the heading saying what happens next. */
  subtitle?: string;
  /** Title-bar text, when it should read differently from the heading. Defaults to `title`. */
  header?: React.ReactNode;
  /** Glyph for the header tile; defaults to the neutral file glyph. */
  iconName?: string;
  iconType?: IKuikaModalIconWrapperProps["iconType"];
  iconIntent?: KuikaModalIconIntent;
  confirmLabel: string;
  /**
   * The create is running: the confirm spins, Cancel stands down and the body spins under the
   * sheet. Ignored when `status` is given.
   */
  isLoading?: boolean;
  /**
   * The stateful create (KuikaButton `status`): the confirm rolls to its loading label, then to its
   * success check or "Try again", and its width follows. While `loading`, Cancel stands down and the
   * body spins, as with `isLoading`. The caller moves it back to `idle`. Omit it and `isLoading`
   * works exactly as before.
   */
  status?: KuikaButtonStatus;
  /** The confirm's own words for each status — "Creating", "Created". See KuikaButton `statusLabels`. */
  statusLabels?: IKuikaButtonStatusLabels;
  /** Form field name. Only matters to callers reading the field back by name. */
  fieldName?: string;
  fieldLabel?: string;
  placeholder?: string;
  /** The entity's own name rules: required, pattern, uniqueness. */
  rules: Rule[];
  /**
   * HTML `maxLength` on the input. No `used / max` count: `KuikaInput` draws its count below
   * the box, and a single-line input clips its own overflow, so the count would be there in
   * the DOM and invisible on screen — only the textarea variant shows one.
   */
  maxLength?: number;
  /**
   * Extra fields rendered under the name, inside the SAME form and the same submit — for
   * a sheet that asks one more thing (an API method's description). Anything beyond a
   * second short field belongs in a modal of its own.
   */
  extraFields?: React.ReactNode;
  /** Prefilled value, re-applied every time the modal opens. */
  initialValue?: string;
  className?: string;
  inputTestId?: string;
  confirmTestId?: string;
  cancelTestId?: string;
  /** On the dialog's wrapper, for a test to scope the sheet's fields and buttons to this modal. */
  wrapTestId?: string;
  onCancel: () => void;
  /** `values` carries the whole form, so a caller with `extraFields` can read them too. */
  onSubmit: (value: string, values: KuikaNameModalFormValues) => void;
}

/** Everything the modal's form collected: the name field plus any `extraFields`. */
export interface KuikaNameModalFormValues {
  [fieldName: string]: string;
}

export class KuikaNameModal extends React.PureComponent<KuikaNameModalProps> {
  static defaultProps: Partial<KuikaNameModalProps> = {
    fieldName: "name",
    fieldLabel: "Name",
    iconName: "file-text",
    iconType: "common"
  };

  formRef = React.createRef<FormInstance>();

  componentDidUpdate(prevProps: KuikaNameModalProps): void {
    // A fresh sheet every time it opens; otherwise the previous attempt's value and its
    // validation state are still sitting there.
    if (this.props.visible && !prevProps.visible) {
      this.formRef.current?.resetFields();
    }
  }

  handleFinish = (values: KuikaNameModalFormValues): void => {
    const value: string = values[this.props.fieldName ?? "name"];
    if (!value) return;
    this.props.onSubmit(value, values);
  };

  handleSubmitClick = (): void => {
    this.formRef.current?.submit();
  };

  render() {
    const fieldName: string = this.props.fieldName ?? "name";
    // A `status` replaces `isLoading`, exactly as it does on KuikaButton.
    const isBusy: boolean =
      this.props.status === undefined ? Boolean(this.props.isLoading) : this.props.status === "loading";

    return (
      <KModal
        showModal={this.props.visible}
        closeModal={this.props.onCancel}
        centered
        width={460}
        isLoading={isBusy}
        className={this.props.className}
        wrapTestId={this.props.wrapTestId}
        // The bar names the dialog and the sheet's own heading repeats it, as every other
        // create modal in the designer does (new branch, new workspace, new folder). An
        // empty bar read as unfinished chrome next to them.
        header={this.props.header ?? this.props.title}
        footer={
          <KModalFooter
            onCancel={this.props.onCancel}
            cancelTestId={this.props.cancelTestId ?? "cancelButton"}
            submitLabel={this.props.confirmLabel}
            submitTestId={this.props.confirmTestId ?? "createButton"}
            onSubmit={this.handleSubmitClick}
            loading={this.props.isLoading}
            status={this.props.status}
            statusLabels={this.props.statusLabels}
          />
        }
      >
        <div className="kuika-name-modal">
          <div className="kuika-name-modal__header">
            <KuikaModalIconWrapper
              iconName={this.props.iconName ?? "file-text"}
              iconType={this.props.iconType}
              intent={this.props.iconIntent ?? "create"}
            />
            <div className="kuika-name-modal__title">
              <KuikaH5>{this.props.title}</KuikaH5>
              {this.props.subtitle && (
                <KuikaText className="kuika-name-modal__subtitle">{this.props.subtitle}</KuikaText>
              )}
            </div>
          </div>

          <div className="kuika-name-modal__content">
            <Form name={`${fieldName}Modal`} ref={this.formRef} onFinish={this.handleFinish} layout="vertical">
              <div className="kuika-name-modal__form-item">
                <Form.Item
                  name={fieldName}
                  label={this.props.fieldLabel}
                  initialValue={this.props.initialValue}
                  rules={this.props.rules}
                >
                  <KuikaInput
                    data-testid={this.props.inputTestId}
                    placeholder={this.props.placeholder}
                    size="md"
                    autoFocus
                    maxLength={this.props.maxLength}
                  />
                </Form.Item>
              </div>
              {this.props.extraFields}
            </Form>
          </div>
        </div>
      </KModal>
    );
  }
}

export default KuikaNameModal;
